import app from 'flarum/forum/app';
import type Mithril from 'mithril';
import Component from 'flarum/common/Component';
import abbreviateNumber from 'flarum/common/utils/abbreviateNumber';
import classList from 'flarum/common/utils/classList';
import icon from 'flarum/common/helpers/icon';
import type GlobalStats from 'flarum/gamilytics/common/models/GlobalStats';
import { slug } from '../../common';

// Make translation calls shorter
const t = app.translator.trans.bind(app.translator);
const prfx = `${slug}.forum.home.stats`;

const STATS_ICONS = {
	users: 'fa-sharp fa-light fa-address-book',
	reviews: 'fa-sharp fa-light fa-heart',
	bookshelves: 'fa-sharp fa-light fa-books',
	discussions: 'fa-sharp fa-light fa-comments',
} as const;

type StatKey = keyof typeof STATS_ICONS;

export default class HomeStats extends Component {
	loading = true;
	stats: GlobalStats | null = null;

	oninit(vnode: Mithril.Vnode) {
		super.oninit(vnode);

		this.refresh();
	}

	view() {
		if (!this.loading && !this.stats) return null;

		return (
			<div className={classList('HomeStats', { 'HomeStats--loading': this.loading })} aria-busy={this.loading}>
				{(Object.keys(STATS_ICONS) as StatKey[]).map((key) => this.statView(key))}
			</div>
		);
	}

	statView(key: StatKey) {
		return (
			<div className={`HomeStats-stat HomeStats-stat--${key}`}>
				<div className="HomeStats-stat-icon">{icon(STATS_ICONS[key])}</div>
				<div className="HomeStats-stat-valueWrapper">
					{this.stats ? this.valueView(key, this.stats) : <span className="HomeStats-stat-skeleton" aria-hidden="true" />}
				</div>
			</div>
		);
	}

	valueView(key: StatKey, stats: GlobalStats) {
		const value = stats.attribute<number>(key) ?? 0;

		return [
			value > 0 && <span className="HomeStats-stat-value">{abbreviateNumber(value)}</span>,
			<span className="HomeStats-stat-label">{t(`${prfx}.${key}`, { count: value })}</span>,
		];
	}

	async refresh() {
		this.loading = true;

		try {
			this.stats = app.store.all<GlobalStats>('global_stats')[0] ?? (await app.store.find<GlobalStats>('gamilytics/globalStats'));
		} catch {
			this.stats = null;
		}

		this.loading = false;
		m.redraw();
	}
}
